// Shared HubSpot form loader. Pulled out of ContactView.jsx so the same
// script fetch can be kicked off early -- from a "Contact us" button's
// onMouseEnter/onTouchStart anywhere on the site (Home.jsx, ContactCTA.jsx,
// MainMenu.jsx's two contact buttons) -- instead of only starting once a
// visitor has already navigated to /contact and ContactView has mounted.
//
// This is the actual fix for "the contact form takes a while to load on
// mobile": on a slow/high-latency mobile connection, waiting until the
// /contact page mounts to even begin the DNS lookup + TLS handshake +
// script download + HubSpot's own form-config fetch means a visitor stares
// at a mostly-blank card for a beat. Warming the script on hover/touch of
// any contact button elsewhere on the site overlaps that latency with
// whatever the visitor is already doing (reading the page, deciding to
// click) instead of adding it after the fact.
//
// Was 'https://js.hsforms.net/forms/embed/8145724.js' -- that's HubSpot's
// portal-bundled "no-code" embed script, built to auto-scan the page for
// class="hs-form-frame" divs and inject a form directly. It doesn't
// reliably expose the generic hbspt.forms.create() JS API this file (and
// ContactView.jsx) actually need -- console logging confirmed window.hbspt
// never got created by it at all, even though the script itself loaded and
// ran. This is HubSpot's actual Forms v2 API loader, the one documented to
// define window.hbspt.forms.create() -- not portal-specific, so the same
// URL works for any form/portal.
export const HUBSPOT_SCRIPT_SRC = 'https://js.hsforms.net/forms/v2.js';
export const HUBSPOT_FORM_ID = '8be3df9c-8a8c-480b-9305-f16d617f3da6';
export const HUBSPOT_PORTAL_ID = '8145724';
export const HUBSPOT_REGION = 'na1';

let hubspotScriptPromise = null;

// Polls for window.hbspt.forms directly rather than trusting the <script>
// tag's own 'load' event -- that event only guarantees the script FILE
// finished executing, not that HubSpot's own setup (if any of it happens
// asynchronously after that) has actually populated window.hbspt.forms yet.
const POLL_INTERVAL_MS = 50;
const POLL_TIMEOUT_MS = 15000;

const waitForHbsptForms = () =>
  new Promise((resolve, reject) => {
    const startedAt = Date.now();
    const check = () => {
      if (window.hbspt?.forms) {
        resolve(window.hbspt);
        return;
      }
      if (Date.now() - startedAt > POLL_TIMEOUT_MS) {
        reject(new Error('Timed out waiting for HubSpot forms script to initialize.'));
        return;
      }
      setTimeout(check, POLL_INTERVAL_MS);
    };
    check();
  });

// Idempotent -- safe to call repeatedly (every hover, every touchstart, and
// again on the /contact page itself). Returns a promise that resolves with
// window.hbspt once its `forms.create` API is actually usable.
export const preloadHubSpotFormsScript = () => {
  if (hubspotScriptPromise) {
    return hubspotScriptPromise;
  }

  const alreadyInserted = document.querySelector(
    `script[src="${HUBSPOT_SCRIPT_SRC}"]`
  );

  if (!alreadyInserted) {
    const script = document.createElement('script');
    script.src = HUBSPOT_SCRIPT_SRC;
    document.body.appendChild(script);
  }

  hubspotScriptPromise = waitForHbsptForms();

  return hubspotScriptPromise;
};
